import { useModel } from "@stem-sims/nexus"
import Assessment from "components/Assignment/Assessment"
import AssignmentLesson from "components/Assignment/AssignmentLesson"
import { toProductString } from "helpers/blockStringConverter"
import { getVideoID } from "models/modules"
import { useEffect, useState } from "react"
import { Button, Form} from "react-bootstrap"
import { useNavigate } from "react-router"
import { BeatLoader } from "react-spinners"
import { toast } from "react-toastify"
import lessons, { LessonResponse } from "shared/lessons"
import assessments, { AssessmentResponse } from "shared/routes/assessments/assessments"
import { AssignmentResponse } from "shared/types/teacherTypes"

interface Props {
    assignment: AssignmentResponse
}

const PreviewReviewAssignment = ({ assignment } : Props) => {
    const navigate = useNavigate()

    const [currentReviewSection, setCurrentReviewSection] = useState<"pretest" | "otherPretest" | "lesson" | "otherLesson" | "assessment" | "otherAssessment" | "completed">("pretest")
    const [videoId, setVideoId] = useState<string>(null)

    const { response: preAssessment, loading: loadingPreAssessment } = useModel({
        model: assessments.findById,
        props: { id: (assignment?.useGrade3 && assignment?.review?.grade3pretestId) ? assignment?.review?.grade3pretestId : (assignment?.review?.pretestId ?? "") },
    })

    const { response: lesson, loading: loadingLesson } = useModel({
        model: lessons.findById,
        props: (assignment?.useGrade3 && assignment?.review?.grade3lessonId) ? assignment?.review?.grade3lessonId : (assignment?.review?.lessonId ?? "")
    })

    const { response: postAssessment, loading: loadingPostAssessment } = useModel({
        model: assessments.findById,
        props: { id: (assignment?.useGrade3 && assignment?.review?.grade3postTestId) ? assignment?.review?.grade3postTestId : (assignment?.review?.postTestId ?? lesson?.assessment?.id) }
    })

    const [otherPreAssessment, setOtherPreAssessment] = useState<AssessmentResponse>(null)
    const [otherLesson, setOtherLesson] = useState<LessonResponse>(null)
    const [otherPostAssessment, setOtherPostAssessment] = useState<AssessmentResponse>(null)
    useEffect (() => { 
        (async () => {
            setOtherPreAssessment(await assessments.findById({ id: (assignment?.useGrade3 || !assignment?.review?.grade3pretestId) ? (assignment?.review?.pretestId ?? "") : (assignment?.review?.grade3pretestId ?? "") }))
            setOtherLesson(await lessons.findById((assignment?.useGrade3 || !assignment?.review?.grade3lessonId) ? (assignment?.review?.lessonId ?? "") : assignment?.review?.grade3lessonId))
            setOtherPostAssessment(await assessments.findById({ id: (assignment?.useGrade3 || !assignment?.review?.grade3postTestId) ? (assignment?.review?.postTestId ?? lesson?.assessment?.id) : assignment?.review?.grade3postTestId }))
        })()
    },[assignment, lesson])

    useEffect(() => {
        if (!lesson) return
        getVideoID(lesson.moduleName).then(({videoPath}) => setVideoId(videoPath))
    }, [lesson])

    if (!assignment || !assignment?.review || assignment.lessonVariety !== "Review") {
        toast.error("Review Assignment is not properly loaded. Please try again.")
        return null
    }

    if (!preAssessment || loadingPreAssessment || !lesson || loadingLesson || !postAssessment || loadingPostAssessment) return <BeatLoader />

  return (
    <div>
        {assignment &&
            <div className="row mark align-content-center">
                <div className="col"/>
                <h1 className="col-auto align-middle">{assignment.review?.title}</h1>
                <div className="col align-self-center d-flex justify-content-center">
                    {(currentReviewSection === "pretest" || currentReviewSection === "otherPretest") && assignment.review?.grade3pretestId &&
                        <Form.Check
                            type="switch"
                            label={`Preview ${assignment.useGrade3 ? "Original" : "Grade 3"} Version`}
                            className="fs-6 row gx-1"
                            onChange={() =>
                                currentReviewSection === "pretest" ? setCurrentReviewSection("otherPretest") : setCurrentReviewSection("pretest")
                            }
                        />
                    }
                    {(currentReviewSection === "lesson" || currentReviewSection === "otherLesson") && assignment.review?.grade3lessonId &&
                        <Form.Check
                            type="switch"
                            label={`Preview ${assignment.useGrade3 ? "Original" : "Grade 3"} Version`}
                            className="fs-6 row gx-1"
                            onChange={() =>
                                currentReviewSection === "lesson" ? setCurrentReviewSection("otherLesson") : setCurrentReviewSection("lesson")
                            }
                        />
                    }
                    {(currentReviewSection === "assessment" || currentReviewSection === "otherAssessment") && assignment.review?.grade3postTestId &&
                        <Form.Check
                            type="switch"
                            label={`Preview ${assignment.useGrade3 ? "Original" : "Grade 3"} Version`}
                            className="fs-6 row gx-1"
                            onChange={() =>
                                currentReviewSection === "assessment" ? setCurrentReviewSection("otherAssessment") : setCurrentReviewSection("assessment")
                            }
                        />
                    }
                </div>
            </div>
        }
        <div>
            {currentReviewSection === "pretest" &&
                <Assessment
                    assessmentId={preAssessment.id}
                    moduleName={preAssessment.module}
                    assessmentNumber={preAssessment.number}
                    type={preAssessment.type}
                    isPartOfTopic={false}
                    oneQuestionMode={assignment.oneQuestionMode}
                    randomizable={assignment.randomizable}
                    isReview={true}
                />
            }

            {currentReviewSection === "otherPretest" && assignment.review?.grade3pretestId &&
                <Assessment
                    assessmentId={otherPreAssessment.id}
                    moduleName={otherPreAssessment.module}
                    assessmentNumber={otherPreAssessment.number}
                    type={otherPreAssessment.type}
                    isPartOfTopic={false}
                    oneQuestionMode={assignment.oneQuestionMode}
                    randomizable={assignment.randomizable}
                    isReview={true}
                />
            }

            {currentReviewSection === "lesson" && lesson &&
                <AssignmentLesson
                    isPartOfTopic={false}
                    moduleName={lesson.moduleName}
                    type={lesson.type}
                    videoId={videoId}
                    hasAssessment={!!lesson.assessmentNumber}
                    lessonVariety={assignment?.lessonVariety}
                    isReview={true}
                    isTeacherReviewPreview={true}
                    externalLessonId={lesson.id}
                />
            }

            {currentReviewSection === "otherLesson" && assignment.review?.grade3lessonId &&
                <AssignmentLesson
                    isPartOfTopic={false}
                    moduleName={otherLesson.moduleName}
                    type={otherLesson.type}
                    videoId={videoId}
                    hasAssessment={!!otherLesson.assessmentNumber}
                    lessonVariety={assignment?.lessonVariety}
                    isReview={true}
                    isTeacherReviewPreview={true}
                    externalLessonId={otherLesson.id}
                />
            }

            {currentReviewSection === "assessment" && lesson &&
                <Assessment
                    assessmentId={postAssessment.id}
                    moduleName={postAssessment.module}
                    assessmentNumber={postAssessment.number}
                    type={postAssessment.type}
                    isPartOfTopic={false}
                    oneQuestionMode={assignment.oneQuestionMode}
                    randomizable={assignment.randomizable}
                    isReview={true}
                />
            }

            {currentReviewSection === "otherAssessment" && lesson && assignment.review?.grade3postTestId &&
                <Assessment
                    assessmentId={otherPostAssessment.id}
                    moduleName={otherPostAssessment.module}
                    assessmentNumber={otherPostAssessment.number}
                    type={otherPostAssessment.type}
                    isPartOfTopic={false}
                    oneQuestionMode={assignment.oneQuestionMode}
                    randomizable={assignment.randomizable}
                    isReview={true}
                />
            }

            {currentReviewSection === "completed" &&
                <div>
                    <h1>Review Completed</h1>
                    <Button 
                        className="me-2"
                        onClick={() => navigate(`/dashboard/teacher/science/${toProductString(assignment.block)}/assignments`)}>
                            <i className="fas fa-right-from-bracket me-1" />
                            <span>Return to Assignments</span>
                    </Button>
                </div>}
        </div>

        <div className="position-fixed bottom-0 start-50 mb-2">
            <Button 
                className="me-2"
                size="lg"
                onClick={() => {
                    if (currentReviewSection === "lesson" || currentReviewSection === "otherLesson") setCurrentReviewSection("pretest")
                    else if (currentReviewSection === "assessment" || currentReviewSection === "otherAssessment") setCurrentReviewSection("lesson")
                    else if (currentReviewSection === "completed") setCurrentReviewSection("assessment")
                }} 
                disabled={currentReviewSection === "pretest" || currentReviewSection === "otherPretest"}>
                    <i className="fas fa-backward me-1" />
                    <span>Previous</span>
            </Button>
            <Button 
                size="lg"
                onClick={() => {
                    if (currentReviewSection === "pretest" || currentReviewSection === "otherPretest") setCurrentReviewSection("lesson")
                    else if (currentReviewSection === "lesson" || currentReviewSection === "otherLesson") setCurrentReviewSection("assessment")
                    else if (currentReviewSection === "assessment" || currentReviewSection === "otherAssessment") setCurrentReviewSection("completed")
                }} 
                disabled={currentReviewSection === "completed"}>
                    <i className="fas fa-forward me-1" />
                    <span>Continue</span>
            </Button>
        </div>
    </div>
  )
}

export default PreviewReviewAssignment
